import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import { Card } from "@magnetic/card";
import { ArrowSquareOut, X } from "@magnetic/icons";
import { Modal } from "@magnetic/modal";
import { Text } from "@magnetic/text";
import { Tooltip } from "@magnetic/tooltip";

import { Button } from "src/components/buttons";
import { EmptyContainer } from "src/components/emptyContainer";
import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core/apiError";
import { DataProviderContext } from "src/core/provider/dataProvider";
import { Bilge } from "src/gen/rest/bilge/BilgeService";
import {
  CliCommandCompletion,
  CliCommandResponse,
  CommandAction
} from "src/gen/schema/bilge/api";
import { NodeType } from "src/gen/schema/models/fabric";

import { useAutoScroll } from "./hooks/useAutoscroll";
import { useStreamingSubscription } from "./hooks/useStreamingSubscription";
import { MockShellHelp } from "./mockShellHelp";
import { MockShellHelpTextOutput } from "./mockShellHelpTextOutput";
import { MockShellHistoryList } from "./mockShellHistoryList";
import { MockShellInput } from "./mockShellInput";
import { MockShellItemHeader } from "./mockShellItemHeader";
import {
  addCommandOutput,
  CommandHistory,
  createInitialHistory,
  MAX_OUTPUT_LINES
} from "./mockShellSessionManager";
import { CommandExecution } from "./mockShellTypes";
import {
  buildMockShellPopOutUrl,
  copyMockshellCmds,
  getDevice
} from "./mockShellUtils";

import "./manageMockShellModal.scss";
interface MockshellProps {
  readonly isPopOut?: boolean;
  readonly onClose: () => void;
  readonly initialCommandOverride?: string;
  readonly initialDeviceIdOverride?: string;
}
export const ManageMockShellModal = (props: MockshellProps) => {
  const { onClose, initialCommandOverride, initialDeviceIdOverride, isPopOut } =
    props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | undefined>();
  const [cmdResult, setCmdResult] = useState<CliCommandResponse | undefined>();
  const [action, setAction] = useState<CommandAction>();
  const [suggestions, setSuggestions] = useState<CliCommandCompletion[]>([]);
  const [isSuggestionError, setIsSuggestionError] = useState<boolean>(false);
  const { fabric } = useContext(DataProviderContext);
  const location = useLocation();
  const [command, setCommand] = useState<string>(initialCommandOverride ?? "");
  const [cursorPos, setCursorPos] = useState<number>(command.length);
  const [previousCommand, setPreviousCommand] = useState<string>("");
  const [executedAt, setExecutedAt] = useState<Date | undefined>();
  const [commandHistory, setCommandHistory] = useState<CommandHistory>(
    createInitialHistory()
  );
  const [scopedDeviceId, setScopedDeviceId] = useState<string>(
    initialDeviceIdOverride ??
      getDevice(location.pathname, fabric)?.deviceId ??
      ""
  );

  const scopedDevice = useMemo(() => {
    if (!scopedDeviceId || !fabric) return undefined;
    return fabric.nodes.find(
      (node) => node.id === scopedDeviceId || node.deviceId === scopedDeviceId
    );
  }, [fabric, scopedDeviceId]);

  // If device is blueprint node or device gets unbound in between the session, then disable mockshell Input and show a message
  const isSelectedDeviceUnavailable = useMemo(() => {
    const hasScopedDevice = Boolean(scopedDeviceId);
    const isScopedDeviceRemoved = Boolean(
      hasScopedDevice && (!fabric || !scopedDevice)
    );
    const isScopedDeviceBlueprint =
      scopedDevice?.nodeType === NodeType.NODE_BLUEPRINT;
    return isScopedDeviceRemoved || isScopedDeviceBlueprint;
  }, [fabric, scopedDevice, scopedDeviceId]);

  const scopedDeviceName = useMemo(() => {
    if (!scopedDeviceId) return "";
    return scopedDevice?.name ?? scopedDeviceId;
  }, [scopedDeviceId, scopedDevice]);

  const {
    streamingOutput,
    isStreamingActive,
    startSession,
    handleStopCommand
  } = useStreamingSubscription({
    fabricId: fabric?.id,
    deviceId: scopedDeviceId,
    previousCommand,
    executedAt,
    onError: setError,
    onCommandHistoryUpdate: setCommandHistory,
    selectedDeviceUnavailable: isSelectedDeviceUnavailable
  });
  const streamingOutputRef = useRef<HTMLDivElement>(null);
  const { outputContainerRef, handleOutputScroll } = useAutoScroll(
    isStreamingActive,
    cmdResult,
    streamingOutput
  );

  useEffect(() => {
    if (!isSelectedDeviceUnavailable) return;

    setCommand("");
    setSuggestions([]);
    setCmdResult(undefined);
    setAction(undefined);
    setLoading(false);
  }, [isSelectedDeviceUnavailable]);

  const popOutUrl = useMemo(() => {
    if (!fabric?.id) return "";
    return buildMockShellPopOutUrl(fabric.id, command, scopedDeviceId);
  }, [fabric?.id, command, scopedDeviceId]);

  const handlePopOut = useCallback(() => {
    if (popOutUrl) {
      window.open(popOutUrl, "_blank");
      onClose();
    }
  }, [popOutUrl, onClose]);

  const handleClose = useCallback(() => {
    if (isStreamingActive) {
      void handleStopCommand().finally(onClose);
      return;
    }
    onClose();
  }, [handleStopCommand, isStreamingActive, onClose]);

  const commandExecution = useCallback(
    ({ cmd, cmdAction, resultFormat, cmdPos }: CommandExecution) => {
      if (fabric && !loading && !isStreamingActive) {
        const payload = {
          command: cmd,
          action: cmdAction,
          fabricId: fabric.id,
          resultFormat,
          cursorPos: cmdPos,
          initialCommand: initialCommandOverride ?? "",
          deviceId: scopedDeviceId
        };
        setLoading(true);
        Bilge.tCliCommand(payload)
          .then((response) => {
            setCmdResult(response);
            switch (cmdAction) {
              case CommandAction.EXECUTE: {
                setCommand("");
                setCursorPos(response.cursorPos);
                setPreviousCommand(cmd);
                setExecutedAt(new Date()); // Capture execution timestamp
                // Handle "scope <device>" — executionResults contains the device ID
                const isScopeCommand = cmd
                  .trim()
                  .toLowerCase()
                  .startsWith("scope ");
                if (isScopeCommand && response.executionResults) {
                  setScopedDeviceId(response.executionResults.trim());
                }
                // Handle "exit" — return to fabric level
                const isExitCommand = cmd.trim().toLowerCase() === "exit";
                if (isExitCommand) {
                  setScopedDeviceId("");
                }
                // Don't add scope/exit commands to history
                const shouldSkipHistory = isScopeCommand || isExitCommand;
                if (response.sessionId) {
                  startSession(response.sessionId, response.deviceId);
                } else if (!shouldSkipHistory) {
                  setCommandHistory((prev) =>
                    addCommandOutput(
                      prev,
                      cmd,
                      response.executionResults,
                      response.resultFormat,
                      undefined,
                      scopedDeviceId
                    )
                  );
                }
                break;
              }
              case CommandAction.EXPOUND:
              case CommandAction.EXPAND:
                // expand should auto complete a command if possible and also provide the list of next available  commands
                setCommand(response.command);
                setCursorPos(response.cursorPos);
                break;
              default:
                break;
            }
          })
          .catch((err) => {
            setError(err);
            // Clear the previous command
            setPreviousCommand(cmd);
            setExecutedAt(new Date()); // Capture execution timestamp for failed commands
            setCmdResult(undefined);
          })
          .finally(() => {
            setAction(cmdAction);
            setLoading(false);
          });
      }
    },
    [
      fabric,
      initialCommandOverride,
      isStreamingActive,
      loading,
      scopedDeviceId,
      startSession
    ]
  );

  const completeCommand = useCallback(
    ({ cmd, resultFormat, cmdPos }: CommandExecution) => {
      if (fabric) {
        const payload = {
          command: cmd,
          action: CommandAction.COMPLETE,
          fabricId: fabric.id,
          resultFormat,
          cursorPos: cmdPos,
          initialCommand: initialCommandOverride ?? "",
          deviceId: scopedDeviceId
        };
        setLoading(true);
        Bilge.tCliCommand(payload)
          .then((response) => {
            setCmdResult(response);
            setCommand(response.command);
            setCursorPos(response.cursorPos);
          })
          .catch((err) => {
            setError(err);
            setIsSuggestionError(true);
            setCmdResult(undefined);
          })
          .finally(() => {
            setAction(CommandAction.COMPLETE);
            setLoading(false);
          });
      }
    },
    [fabric, initialCommandOverride, scopedDeviceId]
  );
  const clearError = useCallback(() => setError(undefined), []);
  const handleStreamingCopy = useCallback(() => {
    copyMockshellCmds(streamingOutputRef);
  }, []);

  const handleCopySession = useCallback(() => {
    copyMockshellCmds(outputContainerRef);
  }, [outputContainerRef]);

  const handleClearSession = useCallback(() => {
    setCommandHistory(createInitialHistory());
    setCmdResult(undefined);
    setAction(undefined);
    setPreviousCommand("");
    setExecutedAt(undefined);
    setError(undefined);
  }, []);

  const showHelpOutput =
    !isSelectedDeviceUnavailable &&
    (action === CommandAction.COMPLETE ||
      action === CommandAction.EXPOUND ||
      action === CommandAction.EXPAND) &&
    cmdResult;
  return (
    <Modal isOpen={true} size={"xl"} className="cnc-manage-mock-shell">
      <HFlex justify="flex-end">
        {commandHistory.commands.length > 0 && (
          <HFlex gap="md">
            <Tooltip title="Copy the whole screen">
              <Button
                name="copy-all-session"
                kind="tertiary"
                size="sm"
                onClick={handleCopySession}
                data-qa="mock-shell-copy-all"
                aria-label="Copy all session"
              >
                Copy all
              </Button>
            </Tooltip>
            <Tooltip title="Clear the whole screen">
              <Button
                name="clear-all-session"
                kind="tertiary"
                size="sm"
                onClick={handleClearSession}
                data-qa="mock-shell-clear-all"
                aria-label="Clear all session output"
                disabled={isStreamingActive}
              >
                Clear all
              </Button>
            </Tooltip>
          </HFlex>
        )}
        <MockShellHelp />
        {!isPopOut && (
          <Tooltip title="Open command in new window">
            <Button
              name="pop-out-mock-shell"
              kind="tertiary"
              size="md"
              onClick={handlePopOut}
              disabled={!popOutUrl || isStreamingActive}
              data-qa="mock-shell-pop-out-button"
              aria-label="Open in new window"
              icon={<ArrowSquareOut size={20} />}
            />
          </Tooltip>
        )}
        <Button
          name="mock-shell-close"
          kind="tertiary"
          size="sm"
          icon={<X size={20} />}
          onClick={handleClose}
          aria-label="Close"
          data-qa="mock-shell-close"
        />
      </HFlex>
      <Modal.Divider size="xs" />
      <Card className="cnc-manage-mock-shell-card">
        <Card.Well
          direction="vertical"
          gap="md"
          className="cnc-manage-mock-shell-container"
        >
          <VFlex className="mock-shell-live-tools-container">
            <div
              className="mock-shell-output-history-container"
              ref={outputContainerRef}
              onScroll={handleOutputScroll}
            >
              {/* History list */}
              <MockShellHistoryList
                history={commandHistory}
                loading={loading}
              />
              {!isSelectedDeviceUnavailable && isStreamingActive && (
                <VFlex
                  className="mock-shell-history-item"
                  data-qa="mock-shell-streaming-output"
                  ref={streamingOutputRef}
                >
                  <MockShellItemHeader
                    command={previousCommand}
                    executedAt={executedAt}
                    showPopOutButton={true}
                    selectedDeviceId={scopedDeviceId}
                    onCopy={handleStreamingCopy}
                  />
                  <pre className="mock-shell-history-item-output">
                    {streamingOutput}
                  </pre>
                  <div className="mock-shell-streaming-dots">
                    <span className="streaming-dot">.</span>
                    <span className="streaming-dot">.</span>
                    <span className="streaming-dot">.</span>
                  </div>
                </VFlex>
              )}

              {/* Help/Suggestions output from Tab key - NOT counted towards 1000 lines */}
              {showHelpOutput && (
                <MockShellHelpTextOutput
                  cmdResult={cmdResult}
                  cmdAction={action}
                />
              )}
              {isSelectedDeviceUnavailable && (
                <EmptyContainer
                  text=""
                  additionalContent={
                    <VFlex
                      gap="sm"
                      align="center"
                      data-qa="quick-command-unavailable-message"
                    >
                      <Text weight="bold" size="p2">
                        Quick Command unavailable for unbound device
                      </Text>
                      <Text weight="light">
                        Bind device to start using Quick Command
                      </Text>
                    </VFlex>
                  }
                />
              )}
              {error && (
                <VFlex className="mock-shell-history-item">
                  <MockShellItemHeader
                    command={previousCommand}
                    executedAt={executedAt}
                    showCopyButton={false}
                    showPopOutButton={false}
                    selectedDeviceId={scopedDeviceId}
                  />
                  <ErrorNotification error={error} onClose={clearError} />
                </VFlex>
              )}
            </div>
            <VFlex className="mock-shell-input-section" gap={"xs"}>
              <MockShellInput
                completeCommand={completeCommand}
                commandExecution={commandExecution}
                commandCompleteSuggestions={suggestions}
                onCommandCompleteSuggestionsChange={setSuggestions}
                onClearError={setError}
                loading={loading}
                isSuggestionError={isSuggestionError}
                setIsSuggestionError={setIsSuggestionError}
                command={command}
                setCommand={setCommand}
                cursorPos={cursorPos}
                setCursorPos={setCursorPos}
                initialCommand={initialCommandOverride ?? ""}
                isBlueprintNode={isSelectedDeviceUnavailable}
                deviceName={scopedDeviceName}
                isStreamingActive={isStreamingActive}
                onStopCommand={handleStopCommand}
              />
              <Text size="p5" color="light" className="mock-shell-info-text">
                {`Max scrollback: ${MAX_OUTPUT_LINES} lines. Command output isn't persistent across CLI sessions.`}
              </Text>
            </VFlex>
          </VFlex>
        </Card.Well>
      </Card>
    </Modal>
  );
};
